// src/components/CommentThread.js
// Updated: 10/22/2025 10:20 PM
// --- COPY-PASTE THIS ENTIRE FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { Send } from 'lucide-react';

// Helper to format the Firestore timestamp
const formatDate = (timestamp) => {
  if (!timestamp) return '...';
  return timestamp.toDate().toLocaleString('en-US', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

export default function CommentThread({ docId }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const { currentUser, userData } = useAuth();

  // Listen for real-time comment updates
  useEffect(() => {
    setIsLoading(true);
    // Create a reference to the 'comments' subcollection for this document
    const commentsRef = collection(db, 'documents', docId, 'comments');
    const q = query(commentsRef, orderBy('createdAt', 'asc')); // Oldest first

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const commentsData = [];
        querySnapshot.forEach((doc) => {
          commentsData.push({ id: doc.id, ...doc.data() });
        });
        setComments(commentsData);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching comments:', error);
        setIsLoading(false);
      }
    );

    // Cleanup listener
    return () => unsubscribe();
  }, [docId]);

  const handlePostComment = async (e) => {
    e.preventDefault();
    if (newComment.trim() === '' || !currentUser || isPosting) return;

    setIsPosting(true);

    try {
      // Add a new document to the 'comments' subcollection
      await addDoc(collection(db, 'documents', docId, 'comments'), {
        text: newComment,
        authorId: currentUser.uid,
        authorName: currentUser.displayName || 'User',
        orgId: userData.orgId, // Good to store this for rules
        createdAt: serverTimestamp(),
      });
      setNewComment(''); // Clear the input
    } catch (err) {
      console.error('Error posting comment:', err);
      alert('Failed to post comment. Please try again.');
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="flex h-full min-h-[500px] flex-col rounded-lg bg-white p-6 shadow-lg">
      <h2 className="mb-4 text-xl font-semibold">Comments</h2>

      {/* Comment List */}
      <div className="flex-1 space-y-4 overflow-y-auto pr-2">
        {isLoading && <p>Loading comments...</p>}
        {!isLoading && comments.length === 0 && (
          <p className="text-gray-500">No comments yet. Be the first!</p>
        )}
        {comments.map((comment) => (
          <div key={comment.id} className="flex space-x-3">
            <div className="flex-shrink-0">
              {/* You could add user avatars here */}
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-sm font-semibold text-white">
                {comment.authorName.charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="rounded-lg bg-gray-100 p-3">
                <p className="text-sm font-medium text-gray-900">
                  {comment.authorName}
                </p>
                <p className="mt-1 text-gray-700">{comment.text}</p>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {formatDate(comment.createdAt)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* New Comment Form */}
      <div className="mt-6 flex-shrink-0">
        <form onSubmit={handlePostComment} className="flex space-x-3">
          <textarea
            rows="2"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add your comment..."
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            disabled={isPosting}
          />
          <button
            type="submit"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            disabled={isPosting || newComment.trim() === ''}
          >
            <Send className="h-5 w-5" />
          </button>
        </form>
      </div>
    </div>
  );
}